import React, { ReactElement, useCallback, useContext, useRef } from 'react'
import { GraphData, GraphProps } from './graph/Graph.d'
import { GraphContextProvided } from '../helpers/context.d'
import { PrimaryAction } from '../helpers/action/Action.d'
import GraphCloud from './graph/GraphCloud'
import GraphBar from './graph/GraphBar'
import GraphLine from './graph/GraphLine'
import dompurify from 'dompurify'

const Graph = ({ data, onClick }: GraphProps) => {
    if (data.graphType === 'cloud') {
        return <GraphCloud data={ data } onClick={ onClick } />
    } else if (data.graphType === 'bar') {
        return <GraphBar data={ data } onClick={ onClick } />
    } else {
        return <GraphLine data={ data } onClick={ onClick } />
    }
}

const DOUBLE_TAP_MS = 300

const GraphWrapper = (props: {
    action: PrimaryAction
    context: React.Context<GraphContextProvided>
    transform?: (data: GraphData) => GraphData }
): ReactElement => {
    const { state, dispatch } = useContext(props.context)
    const key = JSON.stringify(props.action)
    const lastTapTimestamp = useRef<number>(0)
    const onClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) {
            dispatch({ type: 'CLEAR_GRAPH', graph: key })
        } else {
            const now = Date.now()
            if (now - lastTapTimestamp.current < DOUBLE_TAP_MS) {
                dispatch({ type: 'CLEAR_GRAPH', graph: key })
            }
            lastTapTimestamp.current = now
        }
    }, [key, dispatch])
    if (!props.action || !state.graphs || !(key in state.graphs)) {
        return <></>
    }
    const source = state.graphs[key]

    if (!source) {
        return <></>
    } else if (typeof source === 'object') {
        return <Graph data={ props.transform ? props.transform(source) : source } onClick={ onClick } />
    } else if (source.startsWith('<')) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        return <div onClick={ onClick } className="graph" dangerouslySetInnerHTML={ { __html: dompurify.sanitize(source) } } />
    } else {
        return <img onClick={ onClick } className="graph" src={ `data:image/png;base64,${source}` } />
    }
}

export default GraphWrapper
